"use client";

import type { Selection } from "@react-types/shared";

import { Button } from "@heroui/button";
import { Select, SelectItem } from "@heroui/select";
import Image from "next/image";
import { usePathname } from "next/navigation";

import type { MovieSortOptions, SortOption } from "@/types/movie";
import type { LanguageCode } from "@/config/i18n";
import { getLanguageFromPathname } from "@/lib/i18n/paths";
import { createTranslator } from "@/lib/i18n/translations";
import { getDefaultDirection } from "@/lib/movies/browse-params";

interface SortControlProps {
  sortOptions: MovieSortOptions;
  onChange: (options: MovieSortOptions) => void;
  /** Defaults to the language in the current URL. */
  lang?: LanguageCode;
}

export function SortControl({ sortOptions, onChange, lang }: SortControlProps) {
  const pathname = usePathname();
  const t = createTranslator(lang ?? getLanguageFromPathname(pathname));

  const sortByOptions: { key: SortOption; label: string; logo?: string }[] = [
    { key: "imdb", label: t("movies.imdbRating"), logo: "/IMDB-logo.svg" },
    {
      key: "rotten_tomatoes_critic",
      label: t("movies.rtCriticRatingSort"),
      logo: "/rotten-tomatoes-critic-logo.svg",
    },
    {
      key: "rotten_tomatoes_audience",
      label: t("movies.rtAudienceRatingSort"),
      logo: "/rotten-tomatoes-audience-logo.svg",
    },
    { key: "tmdb", label: t("movies.tmdbRating"), logo: "/tmdb-logo.svg" },
    {
      key: "cage_mdb",
      label: t("movies.cageMdbSort"),
      logo: "/nicolas-cage.png",
    },
    { key: "release_date", label: t("movies.releaseDate") },
    { key: "title", label: t("movies.title_sort") },
    { key: "runtime", label: t("movies.runtime") },
    { key: "box_office", label: t("movies.boxOffice") },
    { key: "awards", label: t("movies.awards") },
  ];

  const handleSortByChange = (keys: Selection) => {
    if (keys !== "all" && keys.size > 0) {
      const sortBy = Array.from(keys as Set<string>)[0] as SortOption;

      onChange({ sortBy, direction: getDefaultDirection(sortBy) });
    }
  };

  const toggleDirection = () => {
    onChange({
      ...sortOptions,
      direction: sortOptions.direction === "asc" ? "desc" : "asc",
    });
  };

  const directionLabel =
    sortOptions.direction === "asc"
      ? t("movies.ascending")
      : t("movies.descending");

  return (
    <div className="flex w-full items-center gap-2 md:w-auto">
      <Select
        className="min-w-0 flex-1 md:w-56 md:flex-none"
        classNames={{
          trigger: "h-14 min-h-14",
          listboxWrapper: "!max-h-[min(450px,60vh)]",
        }}
        id="sort-by"
        label={t("movies.sortBy")}
        labelPlacement="inside"
        popoverProps={{
          classNames: {
            content: "p-0 max-w-[calc(100vw-2rem)]",
          },
        }}
        renderValue={(items) => {
          return items.map((item) => {
            const option = sortByOptions.find((opt) => opt.key === item.key);

            if (!option) return null;

            return (
              <div key={item.key} className="flex min-w-0 items-center gap-2">
                {/* Narrow phones need the room for the label */}
                {option.logo && (
                  <Image
                    alt=""
                    className="hidden h-4 w-auto shrink-0 min-[400px]:block"
                    height={20}
                    src={option.logo}
                    width={20}
                  />
                )}
                <span className="truncate">{option.label}</span>
              </div>
            );
          });
        }}
        scrollShadowProps={{
          isEnabled: false,
        }}
        selectedKeys={new Set([sortOptions.sortBy])}
        size="md"
        onSelectionChange={handleSortByChange}
      >
        {sortByOptions.map((option) => (
          <SelectItem
            key={option.key}
            startContent={
              option.logo ? (
                <Image
                  alt=""
                  className="h-4 w-auto"
                  height={20}
                  src={option.logo}
                  width={20}
                />
              ) : null
            }
            textValue={option.label}
          >
            {option.label}
          </SelectItem>
        ))}
      </Select>

      <Button
        isIconOnly
        aria-label={t("movies.sortDirection", { direction: directionLabel })}
        className="h-14 min-h-14 w-12 min-w-12 shrink-0"
        title={directionLabel}
        variant="flat"
        onPress={toggleDirection}
      >
        <span aria-hidden="true">
          {sortOptions.direction === "asc" ? "↑" : "↓"}
        </span>
      </Button>
    </div>
  );
}
